/* eslint-disable max-lines */
import {
  forwardRef,
  MouseEvent,
  PropsWithChildren,
  ReactNode,
  useContext
} from 'react';
import cn from 'classnames';
import { ThemeContext } from '../../../utils/providers/theme-context';
import { ensureBgForWhiteText } from '../../../utils/helpers/color-utils';
import Loader from '../loaders/loader';

/** Canonical variants */
type CanonicalVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'danger'
  | 'link'
  | 'quiet';

/** Legacy aliases — still accepted, mapped internally to canonical variants */
type LegacyVariant =
  | 'success'
  | 'gray'
  | 'light'
  | 'text'
  | 'outline'
  | 'smoke';

export type VariantType = CanonicalVariant | LegacyVariant;

/** Canonical sizes — use these going forward */
type CanonicalSize = 'sm' | 'md' | 'lg';

/** Legacy size names — still accepted */
type LegacySize = 'xs' | 'small' | 'medium' | 'large' | 'unset';

type SizeType = CanonicalSize | LegacySize;

interface Props {
  label?: string | ReactNode;
  icon?: ReactNode;
  iconPosition?: 'left' | 'right';
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  variant?: VariantType;
  type?: 'button' | 'submit' | 'reset';
  size?: SizeType;
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  form?: string;
  color?: string;
  title?: string;
  /**
   * Accessible name for icon-only buttons. TypeScript / JSX accepts
   * any `aria-*` attribute on a component without typing it, so the
   * previous Props silently dropped `aria-label` passed by callers —
   * icon-only toolbar buttons (bold, italic, prev/next match, etc.)
   * reached screen readers as unnamed buttons (#2231 reopen comment).
   * Typing it explicitly here forces callers to opt in and lets the
   * render path forward it to the DOM `<button>`.
   */
  'aria-label'?: string;
  /** Points to another element describing this button (icon-only). */
  'aria-describedby'?: string;
  /** Reflects pressed state for icon-only toggle buttons. */
  'aria-pressed'?: boolean;
  /** Reflects expanded state for buttons that open menus / popovers. */
  'aria-expanded'?: boolean;
  /** Associates the button with its controlled region (popover id). */
  'aria-controls'?: string;
}

function resolveVariant(v: VariantType): CanonicalVariant {
  switch (v) {
    case 'success':
      return 'primary';
    case 'gray':
    case 'light':
    case 'text':
    case 'smoke':
      return 'ghost';
    case 'outline':
      return 'secondary';
    default:
      return v;
  }
}

function resolveSize(s: SizeType): CanonicalSize | 'xs' | 'unset' {
  switch (s) {
    case 'small':
      return 'sm';
    case 'medium':
      return 'md';
    case 'large':
      return 'lg';
    default:
      return s;
  }
}

const Button = forwardRef<HTMLButtonElement, PropsWithChildren<Props>>(
  (
    {
      children,
      label,
      icon,
      iconPosition = 'left',
      onClick,
      variant = 'primary',
      size = 'md',
      type = 'button',
      disabled,
      loading,
      className,
      form,
      color,
      title,
      'aria-label': ariaLabel,
      'aria-describedby': ariaDescribedBy,
      'aria-pressed': ariaPressed,
      'aria-expanded': ariaExpanded,
      'aria-controls': ariaControls
    },
    ref
  ) => {
    const themeContext = useContext(ThemeContext);
    const theme = themeContext?.theme ?? 'light';
    const getColour = themeContext?.getColour;
    const getBrandTint = themeContext?.getBrandTint;

    const pick = (light: string, dark: string) =>
      theme === 'dark' ? dark : light;

    const active = getColour?.('activeText') ?? 'inherit';
    const text = getColour?.('normalText') ?? 'inherit';
    const border = getColour?.('border') ?? 'inherit';

    const canonical = resolveVariant(variant);

    const getVariantVars = (v: CanonicalVariant): React.CSSProperties => {
      switch (v) {
        case 'primary': {
          const safeBg = ensureBgForWhiteText(active);
          const hoverBg =
            theme === 'dark' ? lighten(safeBg, 15) : darken(safeBg, 10);
          return {
            '--btn-bg': safeBg,
            '--btn-bg-hover': hoverBg,
            // eslint-disable-next-line no-restricted-syntax -- constant white for WCAG AA on darkened brand bg
            '--btn-fg': '#ffffff',
            '--btn-border': safeBg,
            '--btn-outline': safeBg
          } as React.CSSProperties;
        }
        case 'secondary': {
          const tint = getBrandTint
            ? getBrandTint(0.08)
            : 'rgba(37,99,235,0.08)';
          return {
            '--btn-bg': 'transparent',
            '--btn-bg-hover': tint,
            '--btn-fg': active,
            '--btn-border': active,
            '--btn-outline': active
          } as React.CSSProperties;
        }
        case 'ghost': {
          const hoverBg = pick('rgba(0,0,0,0.05)', 'rgba(255,255,255,0.08)');
          return {
            '--btn-bg': 'transparent',
            '--btn-bg-hover': hoverBg,
            '--btn-fg': text,
            '--btn-border': 'transparent',
            '--btn-outline': border
          } as React.CSSProperties;
        }
        case 'danger': {
          const dangerBase = getColour?.('danger');
          const dangerBg = dangerBase ?? 'red';
          const dangerHover = dangerBase
            ? darken(dangerBase, theme === 'dark' ? 5 : 10)
            : 'darkred';
          return {
            '--btn-bg': dangerBg,
            '--btn-bg-hover': dangerHover,
            '--btn-fg': 'white',
            '--btn-border': dangerBg,
            '--btn-outline': dangerBg
          } as React.CSSProperties;
        }
        case 'link': {
          return {
            '--btn-bg': 'transparent',
            '--btn-bg-hover': 'transparent',
            '--btn-fg': active,
            '--btn-border': 'transparent',
            '--btn-outline': active
          } as React.CSSProperties;
        }
        case 'quiet': {
          // Neutral outline — gray border, gray text, transparent bg.
          // Reads as a low-emphasis alternative to `secondary` (which uses
          // the brand accent color).
          const inactive = getColour?.('inactiveText') ?? text;
          const hoverBg = pick('rgba(0,0,0,0.04)', 'rgba(255,255,255,0.06)');
          return {
            '--btn-bg': 'transparent',
            '--btn-bg-hover': hoverBg,
            '--btn-fg': inactive,
            '--btn-border': border,
            '--btn-outline': border
          } as React.CSSProperties;
        }
      }
    };

    const getVariantClasses = (v: CanonicalVariant) =>
      cn(
        'bg-[var(--btn-bg)] text-[var(--btn-fg)] border border-[var(--btn-border)]',
        'hover:bg-[var(--btn-bg-hover)]',
        v !== 'link' && 'hover:-translate-y-[1px]',
        v !== 'link' &&
          v !== 'ghost' &&
          'shadow-[0_2px_6px_rgba(0,0,0,0.10),0_1px_3px_rgba(0,0,0,0.06)]',
        v !== 'link' &&
          v !== 'ghost' &&
          'hover:shadow-[0_6px_16px_rgba(0,0,0,0.16),0_2px_5px_rgba(0,0,0,0.10)]',
        'active:translate-y-[1px] active:shadow-[0_1px_2px_rgba(0,0,0,0.08)]',
        v === 'link' && 'hover:underline',
        'focus-visible:outline-[var(--btn-outline)]'
      );

    const hasVisibleLabel =
      label &&
      (typeof label === 'string' ||
        (typeof label === 'object' && 'props' in label));

    const getSizeClasses = (s: SizeType) => {
      const resolved = resolveSize(s);
      // When both icon and label are set, the label text is hidden
      // below 1080px (see the label span below) so the button renders
      // as icon-only on mobile. Mirror that in the padding so a
      // "New user" button doesn't leave 100px of blank space to the
      // right of the plus icon.
      const iconOnlyOnMobile = hasVisibleLabel && !!icon;
      switch (resolved) {
        case 'xs':
          return 'p-2 text-sm';
        case 'sm':
          if (iconOnlyOnMobile) {
            return 'px-2 py-1.5 text-sm min-[1080px]:px-4 min-[1080px]:min-w-[80px]';
          }
          return hasVisibleLabel
            ? 'px-4 py-1.5 text-sm min-w-[80px]'
            : 'px-2 py-1.5 text-sm';
        case 'lg':
          if (iconOnlyOnMobile) {
            return 'px-3 py-2 text-base min-[1080px]:px-6 min-[1080px]:min-w-[120px]';
          }
          return hasVisibleLabel
            ? 'px-6 py-2 text-base min-w-[120px]'
            : 'px-3 py-2 text-base';
        case 'unset':
          return '';
        case 'md':
        default:
          if (iconOnlyOnMobile) {
            return 'px-3 py-1.5 text-sm min-[1080px]:px-5 min-[1080px]:min-w-[100px]';
          }
          return hasVisibleLabel
            ? 'px-5 py-1.5 text-sm min-w-[100px]'
            : 'px-3 py-1.5 text-sm';
      }
    };

    const loaderColor = (() => {
      switch (canonical) {
        case 'primary':
        case 'danger':
          return 'white';
        case 'secondary':
        case 'link':
          return active;
        default:
          return getColour?.('normalText') ?? 'inherit';
      }
    })();

    const resolvedSize = resolveSize(size);
    const loaderHeight =
      resolvedSize === 'sm' || size === 'xs'
        ? 16
        : resolvedSize === 'lg'
          ? 20
          : 18;

    const hasJustifyOverride = /\bjustify-/.test(className ?? '');

    return (
      <button
        ref={ref}
        type={type}
        form={form}
        className={cn(
          'inline-flex items-center rounded-full font-semibold transition-all duration-200 cursor-pointer',
          !hasJustifyOverride && 'justify-start',
          'focus-visible:outline-2 focus-visible:outline-offset-2',
          'disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100 disabled:hover:translate-y-0 disabled:hover:shadow-none',
          getVariantClasses(canonical),
          getSizeClasses(size),
          className
        )}
        style={{
          ...getVariantVars(canonical),
          ...(color ? { color } : {})
        }}
        onClick={onClick}
        disabled={disabled || loading}
        title={title}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        aria-pressed={ariaPressed}
        aria-expanded={ariaExpanded}
        aria-controls={ariaControls}
      >
        {loading ? (
          <Loader width={1} height={loaderHeight} color={loaderColor} />
        ) : (
          <>
            {icon && iconPosition === 'left' && (
              <span className="flex items-center">{icon}</span>
            )}
            {label && (
              <span
                className={cn(
                  'truncate',
                  // With an icon, hide the label below 1080px and show it
                  // from 1080px onwards; icon-only buttons remain unaffected.
                  icon ? 'hidden min-[1080px]:block' : 'block',
                  icon && iconPosition === 'left' && 'min-[1080px]:ml-2',
                  icon && iconPosition === 'right' && 'min-[1080px]:mr-2'
                )}
                title={typeof label === 'string' ? label : undefined}
              >
                {label}
              </span>
            )}
            {icon && iconPosition === 'right' && (
              <span className="flex items-center">{icon}</span>
            )}
            {children}
          </>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';
export default Button;

function darken(hex: string, pct: number): string {
  const n = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * pct);
  const r = Math.max(0, (n >> 16) - amt);
  const g = Math.max(0, ((n >> 8) & 0xff) - amt);
  const b = Math.max(0, (n & 0xff) - amt);
  return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

function lighten(hex: string, pct: number): string {
  const n = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * pct);
  const r = Math.min(255, (n >> 16) + amt);
  const g = Math.min(255, ((n >> 8) & 0xff) + amt);
  const b = Math.min(255, (n & 0xff) + amt);
  return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}
